import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import { useRestaurant } from "./RestaurantContext";
import DeleteManagerModal from "./DeleteManagerModal";

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  managerRestaurantId?: number | null;
  avatarUrl?: string | null;
}

interface Invite {
  id: number;
  email: string;
  status: string;
  createdAt: string;
}

// Delegated manager permissions (Fred, 2026-09-15): QR management,
// landing-page editing, campaign analytics, and Review Card ordering
// (2026-09-17), per location, since a manager can in principle hold more
// than one under the same admin.
type ManagerPermissionKey = "canManageQr" | "canEditLandingPage" | "canViewAnalytics" | "canOrderCards";
const MANAGER_PERMISSION_KEYS: ManagerPermissionKey[] = ["canManageQr", "canEditLandingPage", "canViewAnalytics", "canOrderCards"];
interface ManagerLocationPermissions {
  restaurantId: number;
  restaurantName: string;
  canManageQr: boolean;
  canEditLandingPage: boolean;
  canViewAnalytics: boolean;
  canOrderCards: boolean;
}

const STATUS_KEYS: Record<string, string> = {
  pending: "common.pending",
  accepted: "common.accepted",
  revoked: "common.revoked",
};

const Users: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const { currentRestaurantId: restaurantId, loading: restaurantLoading, canWrite } = useRestaurant();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [managerLocations, setManagerLocations] = useState<ManagerLocationPermissions[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSavingKey, setPermissionsSavingKey] = useState<string | null>(null);
  const [permissionsError, setPermissionsError] = useState("");

  useEffect(() => {
    // Wait for RestaurantContext's own fetch, but only when we truly have
    // nothing yet - see the matching comment in Reviews.tsx. A returning
    // user already has restaurantId from localStorage synchronously.
    if (restaurantLoading && !restaurantId) return;

    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        if (!restaurantId) {
          setError(t("users.createRestaurantFirst"));
          setLoading(false);
          return;
        }

        // Fetch users
        const resUsers = await apiFetch("/api/users");
        if (!resUsers) return;
        const dataUsers = await resUsers.json();
        // Filter: exclude superadmin, only users for the currently selected restaurant
        const filtered = dataUsers.filter((u: User) =>
          u.role !== "superadmin" &&
          ((u.role === "admin" && u.id === user.id) ||
            (u.role === "manager" && u.managerRestaurantId === restaurantId))
        );
        setUsers(filtered);

        // Fetch invites
        const resInvites = await apiFetch(`/api/restaurants/${restaurantId}/invites`);
        if (resInvites) {
          const dataInvites = await resInvites.json();
          setInvites(dataInvites);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(t("users.failedToFetch"));
        setLoading(false);
      }
    };
    setLoading(true);
    fetchData();
  }, [t, restaurantId, restaurantLoading]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !restaurantId) return;
    
    setInviting(true);
    try {
      const response = await apiFetch("/api/users/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail, restaurantId })
      });
      
      if (response) {
        setInviteEmail("");
        // Refresh the invites list
        const resInvites = await apiFetch(`/api/restaurants/${restaurantId}/invites`);
        if (resInvites) {
          const dataInvites = await resInvites.json();
          setInvites(dataInvites);
        }
      }
    } catch (err) {
      console.error("Error sending invite:", err);
    } finally {
      setInviting(false);
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditName(user.name || "");
    setEditActive(user.isActive);
    setEditError("");
    setManagerLocations([]);
    setPermissionsError("");
    if (user.role === "manager") {
      setPermissionsLoading(true);
      apiFetch(`/api/users/${user.id}/manager-locations`)
        .then((res) => (res && res.ok ? res.json() : []))
        .then(setManagerLocations)
        .catch(() => setPermissionsError(t("users.failedLoadPermissions")))
        .finally(() => setPermissionsLoading(false));
    }
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditName("");
    setEditActive(true);
    setEditError("");
    setManagerLocations([]);
    setPermissionsError("");
  };

  // Saves immediately on toggle, one flag at a time. It's a different action
  // from the name/status "Save" button below, and per-location rather than
  // per-manager, so it doesn't fit that same submit.
  const togglePermission = async (restaurantId: number, key: ManagerPermissionKey, value: boolean) => {
    if (!editingUser) return;
    setPermissionsSavingKey(`${restaurantId}-${key}`);
    setPermissionsError("");
    try {
      const res = await apiFetch(`/api/restaurants/${restaurantId}/managers/${editingUser.id}/permissions`, {
        method: "PATCH",
        body: JSON.stringify({ [key]: value }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json() : {};
        setPermissionsError(data.error || t("users.failedUpdatePermissions"));
      } else {
        const updated = await res.json();
        setManagerLocations((locs) =>
          locs.map((l) => (l.restaurantId === restaurantId ? { ...l, ...updated } : l))
        );
      }
    } catch {
      setPermissionsError(t("users.failedUpdatePermissions"));
    }
    setPermissionsSavingKey(null);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    setEditLoading(true);
    setEditError("");
    try {
      const res = await apiFetch(`/api/users/${editingUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: editName, isActive: editActive }),
      });
      if (!res.ok) {
        const data = await res.json();
        setEditError(data.error || t('users.failedUpdateUser'));
      } else {
        // Refresh users list
        const resUsers = await apiFetch("/api/users");
        if (resUsers) {
          const dataUsers = await resUsers.json();
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          const filtered = dataUsers.filter((u: User) =>
            u.role !== "superadmin" &&
            ((u.role === "admin" && u.id === user.id) ||
              (u.role === "manager" && u.managerRestaurantId === restaurantId))
          );
          setUsers(filtered);
        }
        closeEditModal();
      }
    } catch {
      setEditError(t('users.failedUpdateUser'));
    }
    setEditLoading(false);
  };

  const openResetModal = (userId: number) => {
    setResetUserId(userId);
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetUserId(null);
  };

  const confirmResetPassword = async () => {
    if (resetUserId !== null) {
      await handleResetPassword(resetUserId);
      closeResetModal();
    }
  };

  const handleResetPassword = async (userId: number) => {
    setResetLoading(true);
    setResetError("");
    setResetSuccess("");
    
    try {
      const res = await apiFetch(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!res.ok) {
        const data = await res.json();
        setResetError(data.error || t('users.failedResetPassword'));
      } else {
        const data = await res.json();
        setResetSuccess(data.emailed ? t('users.passwordResetEmailed') : t('users.passwordResetEmailFailed'));
        // Clear success message after 10 seconds
        setTimeout(() => setResetSuccess(""), 10000);
      }
    } catch {
      setResetError(t('users.failedResetPassword'));
    }
    setResetLoading(false);
  };

  if (loading) return <div className="p-4 text-center">{t("users.loading")}</div>;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;

  return (
  <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">{t("users.management")}</h1>

      {/* Invite Form */}
      <div className="mb-6 p-4 bg-stone-50 rounded-lg">
        <h2 className="text-lg font-semibold mb-3">{t("users.inviteManager")}</h2>
        <form onSubmit={handleInvite} className="flex gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder={t("users.enterManagerEmail")}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            required
            disabled={!canWrite}
          />
          <button
            type="submit"
            disabled={inviting || !inviteEmail.trim() || !canWrite}
            title={canWrite ? undefined : t("readOnly.disabledHint")}
            className="px-2 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {inviting ? "..." : t("users.invite")}
          </button>
        </form>
      </div>
      {/* Pending Invites Section */}
      {invites.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">{t("users.pendingInvites")}</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded shadow">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">{t("common.email")}</th>
                  <th className="px-4 py-2 text-left">{t("common.status")}</th>
                  <th className="px-4 py-2 text-left">{t("users.invitedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id} className="border-t">
                    <td className="px-4 py-2">{invite.email}</td>
                    <td className="px-4 py-2 capitalize">{t(STATUS_KEYS[invite.status] || invite.status)}</td>
                    <td className="px-4 py-2">{new Date(invite.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Success/Error Messages */}
      {resetSuccess && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {resetSuccess}
        </div>
      )}
      {resetError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {resetError}
        </div>
      )}
      
      {/* Active Users Section */}
      {/* Responsive: Cards on mobile, table on desktop */}
      <div className="block md:hidden space-y-4">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded shadow p-4">
            <div className="flex justify-between mb-2">
              <span className="font-semibold">{t("common.name")}</span>
              <span>{user.name}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">{t("common.email")}</span>
              <span>{user.email}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold">{t("common.role")}</span>
              <span>{t(`roles.${user.role}`, { defaultValue: user.role })}</span>
            </div>
            {user.role === 'manager' && (
              <div className="flex justify-between mb-2">
                <span className="font-semibold">{t("common.status")}</span>
                <span className={user.isActive ? "text-green-600" : "text-gray-400"}>{user.isActive ? t("common.active") : t("common.inactive")}</span>
              </div>
            )}
            <div className="flex justify-between items-center gap-2 mt-2">
              <span className="font-semibold">{t("common.actions")}</span>
              <span className="flex gap-2">
                <button className="px-3 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 text-xs" onClick={() => openEditModal(user)}>{t("common.edit")}</button>
                {user.role === 'manager' && (
                  <button
                    className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-xs"
                    onClick={() => openResetModal(user.id)}
                    disabled={resetLoading}
                  >
                    {resetLoading ? "..." : t("users.resetPw")}
                  </button>
                )}
                {user.role === 'manager' && (
                  <button className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs" onClick={() => setDeletingUser(user)}>{t("users.delete")}</button>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">{t("common.name")}</th>
              <th className="px-4 py-2 text-left">{t("common.email")}</th>
              <th className="px-4 py-2 text-left">{t("common.role")}</th>
              <th className="px-4 py-2 text-left">{t("common.status")}</th>
              <th className="px-4 py-2 text-left">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="px-4 py-2">{user.name}</td>
                <td className="px-4 py-2">{user.email}</td>
                <td className="px-4 py-2">{t(`roles.${user.role}`, { defaultValue: user.role })}</td>
                <td className="px-4 py-2">
                  {user.role === 'manager' ? (
                    <span className={user.isActive ? "text-green-600" : "text-gray-400"}>
                      {user.isActive ? t("common.active") : t("common.inactive")}
                    </span>
                  ) : (
                    <span className="text-teal-600">{t("users.alwaysActive")}</span>
                  )}
                </td>
                <td className="px-4 py-2 flex gap-2">
                  <button className="px-3 py-1 bg-teal-500 text-white rounded hover:bg-teal-600 text-xs" onClick={() => openEditModal(user)}>{t("common.edit")}</button>
                  {user.role === 'manager' && (
                    <button
                      className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-xs"
                      onClick={() => openResetModal(user.id)}
                      disabled={resetLoading}
                    >
                      {resetLoading ? "..." : t("users.resetPw")}
                    </button>
                  )}
                  {user.role === 'manager' && (
                    <button className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs" onClick={() => setDeletingUser(user)}>{t("users.delete")}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-auto">
            <h2 className="text-xl font-bold mb-4">{t("users.editUser")}</h2>
            <label className="block mb-2 font-medium">{t("common.name")}</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 mb-4"
              value={editName}
              onChange={e => setEditName(e.target.value)}
              disabled={editLoading}
            />
            {editingUser?.role === 'manager' && (
              <>
                <label className="block mb-2 font-medium">{t("common.status")}</label>
                <select
                  className="w-full border rounded px-3 py-2 mb-4"
                  value={editActive ? "active" : "inactive"}
                  onChange={e => setEditActive(e.target.value === "active")}
                  disabled={editLoading}
                >
                  <option value="active">{t("common.active")}</option>
                  <option value="inactive">{t("common.inactive")}</option>
                </select>
              </>
            )}
            {editError && <div className="text-red-600 mb-2">{editError}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                {t("common.cancel")}
              </button>
              <button
                className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                onClick={handleEditSave}
                disabled={editLoading || !editName.trim()}
              >
                {editLoading ? t("common.saving") : t("common.save")}
              </button>
            </div>

            {editingUser?.role === "manager" && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="font-medium mb-1">{t("users.delegatedPermissions")}</h3>
                <p className="text-xs text-gray-500 mb-3">{t("users.delegatedPermissionsHint")}</p>
                {permissionsLoading ? (
                  <div className="text-sm text-gray-500">{t("users.loadingPermissions")}</div>
                ) : managerLocations.length === 0 ? (
                  <div className="text-sm text-gray-400">{t("users.noLocationsForPermissions")}</div>
                ) : (
                  <div className="space-y-3">
                    {managerLocations.map((loc) => (
                      <div key={loc.restaurantId} className="bg-stone-50 rounded p-3">
                        {managerLocations.length > 1 && (
                          <div className="text-sm font-medium mb-2">{loc.restaurantName}</div>
                        )}
                        {MANAGER_PERMISSION_KEYS.map((key) => (
                          <label key={key} className="flex items-center justify-between text-sm py-1 cursor-pointer">
                            <span>{t(`users.permission.${key}`)}</span>
                            <input
                              type="checkbox"
                              checked={loc[key]}
                              disabled={permissionsSavingKey === `${loc.restaurantId}-${key}`}
                              onChange={(e) => togglePermission(loc.restaurantId, key, e.target.checked)}
                              className="w-4 h-4"
                            />
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {permissionsError && <div className="text-red-600 text-sm mt-2">{permissionsError}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal for Password Reset */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-auto">
            <h2 className="text-xl font-bold mb-4">{t("users.resetManagerPassword")}</h2>
            <p className="mb-4">{t("users.resetManagerConfirm")}</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={closeResetModal}
                disabled={resetLoading}
              >
                {t("common.cancel")}
              </button>
              <button
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
                onClick={confirmResetPassword}
                disabled={resetLoading}
              >
                {resetLoading ? t("users.resetting") : t("users.resetPassword")}
              </button>
            </div>
          </div>
        </div>
      )}
      {deletingUser && (
        <DeleteManagerModal
          user={deletingUser}
          onDeleted={(id) => setUsers((list) => list.filter((u) => u.id !== id))}
          onClose={() => setDeletingUser(null)}
        />
      )}
  </div>
);
};

export default Users; 